import { FormattedMessage } from "react-intl";
import { Navigate, Route, Routes } from "react-router-dom";

import { SidebarSectionHeader, SidebarV2 } from "common/sidebar";
import { useHeadingDetails } from "common/settingsv2/common";
import { usePermissions } from "common/core/current_user_role";
import { Feature } from "graphql_globals";
import { useFeatureFlag } from "common/feature_gating";

import {
  OrganizationSettings,
  SettingsSidebarOrgLink,
  ORG_ROUTE,
} from "../sidebar_settings/organization";
import {
  OrganizationOverview,
  OrganizationOverviewTab,
  ORG_OVERVIEW_ROUTE,
} from "../sidebar_settings/organization/overview";
import {
  OrganizationSharedInbox,
  OrganizationSharedInboxTab,
  ORG_SHARED_INBOX_ROUTE,
} from "../sidebar_settings/organization/shared_inbox";
import {
  OrganizationEligibility,
  OrganizationEligibilityTab,
  ORG_ELIGIBILITY_ROUTE,
} from "../sidebar_settings/organization/eligibility";
import {
  BillingSettings,
  SettingsSidebarBillingLink,
  BILLING_ROUTE,
} from "../sidebar_settings/billing";
import {
  BILLING_PLAN_DETAILS_ROUTE,
  PlanDetails,
  PlanDetailsTab,
} from "../sidebar_settings/billing/plan_details";
import {
  PaymentSettings,
  PaymentSettingsTab,
  BILLING_PAYMENT_SETTINGS_ROUTE,
} from "../sidebar_settings/billing/payment_settings";
import {
  BillingContact,
  BillingContactTab,
  BILLING_BILLING_CONTACT_ROUTE,
} from "../sidebar_settings/billing/billing_contact";
import {
  RecentPayments,
  RecentPaymentsTab,
  BILLING_RECENT_PAYMENTS_ROUTE,
} from "../sidebar_settings/billing/recent_payments";
import {
  TransactionSettings,
  SettingsSidebarTransactionsLink,
  TRANSACTIONS_ROUTE,
} from "../sidebar_settings/transaction";
import {
  SecuritySettings,
  SettingsSidebarSecurityLink,
  SECURITY_ROUTE,
} from "../sidebar_settings/security";
import LoginSecurity from "../sidebar_settings/security/login_security";
import {
  SignerSettings,
  SignerSettingsTab,
  TRANSACTIONS_SIGNER_SETTINGS_ROUTE,
} from "../sidebar_settings/transaction/signer_settings";
import {
  NotaryInstructions,
  NotaryInstructionsTab,
  TRANSACTIONS_NOTARY_INSTRUCTIONS_ROUTE,
} from "../sidebar_settings/transaction/notary_instructions";
import { TermsSettings, TERMS_ROUTE } from "../sidebar_settings/terms";
import { TermsAndConditions } from "../sidebar_settings/terms/terms";
import {
  BrandSettings as BrandSettingsWrapper,
  SettingsSidebarBrandCustomizationLink,
  BRAND_SETTINGS_ROOT_PREFIX,
} from "../sidebar_settings/brand";
import { BrandSettings, BRAND_SETTINGS_MAIN_ROUTE } from "../sidebar_settings/brand/settings";
import {
  LogoAndHeaderTab,
  LogoAndHeaderBrandSettings,
  LOGO_HEADER_BRAND_ROUTE,
} from "../sidebar_settings/brand/settings/logo_and_header";
import {
  BrandColorsTab,
  BrandColorsSettings,
  BRAND_COLORS_ROUTE,
} from "../sidebar_settings/brand/settings/brand_colors";
import {
  NotaryBackgroundTab,
  NotaryBackgroundSettings,
  NOTARY_BG_ROUTE,
  useIsOrganizationBackgroundSettingsEnabled,
} from "../sidebar_settings/brand/settings/notary_background";
import {
  IntegrationsSettings as IntegrationSettingsContainer,
  INTEGRATIONS_ROUTE,
  SettingsSidebarIntegrationsLink,
} from "../sidebar_settings/integrations";
import IntegrationSettings from "../sidebar_settings/integrations/settings";
import {
  API_KEYS_SETTINGS_ROOT_PREFIX,
  SettingsSidebarApiKeysLink,
  ApiKeys,
} from "../sidebar_settings/api_keys";
import {
  CustomizeLandingPage,
  BRAND_SETTINGS_CUSTOMIZE_LANDING_ROUTE,
  CustomizeLandingPageTab,
} from "../sidebar_settings/brand/customization/landing_page";
import {
  OAUTH_SETTINGS_ROOT_PREFIX,
  SettingsSidebarOAuthLink,
  OAuthSettings,
} from "../sidebar_settings/oauth";
import {
  BILLING_SETTINGS_ROUTE,
  BillingSettingsTab,
  BillingSettings as BillingSettingsV2,
} from "../sidebar_settings/billing/billing_settings";
import {
  PAYER_SETTINGS_ROUTE,
  PayerSettings,
  PayerSettingsTab,
} from "../sidebar_settings/billing/payer_settings";

function TitleAdminSidebar({
  orgName,
  gateApi,
  featureList,
}: {
  orgName: string | null;
  gateApi: boolean;
  featureList: Feature[];
}) {
  const { hasPermissionFor } = usePermissions();
  const oauthApiEnabled = useFeatureFlag("oauth-api");

  return (
    <SidebarV2
      title={
        <FormattedMessage id="75f8f276-419a-4839-9ee5-df88092adddb" defaultMessage="Settings" />
      }
    >
      <SidebarSectionHeader title={orgName} />
      <SettingsSidebarOrgLink />
      <SettingsSidebarBillingLink />
      <SettingsSidebarTransactionsLink />
      <SettingsSidebarSecurityLink />
      <SettingsSidebarBrandCustomizationLink />
      <SettingsSidebarIntegrationsLink />
      {featureList.includes(Feature.PARTNER_API) && hasPermissionFor("viewOrganizationApiKeys") && (
        <SettingsSidebarApiKeysLink showUpgradeBadge={gateApi} />
      )}
      {featureList.includes(Feature.PARTNER_API) &&
        featureList.includes(Feature.API_KEY_MANAGEMENT) &&
        oauthApiEnabled &&
        hasPermissionFor("viewOrganizationApiKeys") && <SettingsSidebarOAuthLink />}
    </SidebarV2>
  );
}

export default function TitleAdminSettings(props: {
  organizationFeatureList: Feature[];
  showApi: boolean;
}) {
  const { gateApi, orgName, featureList, companyBillingAndPricingEnabled } = useHeadingDetails();
  const isOrgNotaryBackgroundsEnabled = useIsOrganizationBackgroundSettingsEnabled(
    props.organizationFeatureList,
  );

  const { hasPermissionFor } = usePermissions();
  return (
    <>
      <TitleAdminSidebar orgName={orgName} gateApi={gateApi} featureList={featureList} />
      <Routes>
        <Route
          path={ORG_ROUTE}
          element={
            <OrganizationSettings
              tabs={
                <>
                  <OrganizationOverviewTab />
                  <OrganizationSharedInboxTab />
                  <OrganizationEligibilityTab />
                </>
              }
            />
          }
        >
          <Route path={ORG_OVERVIEW_ROUTE} element={<OrganizationOverview showAltaIdForm />} />
          <Route path={ORG_SHARED_INBOX_ROUTE} element={<OrganizationSharedInbox />} />
          <Route path={ORG_ELIGIBILITY_ROUTE} element={<OrganizationEligibility />} />
          <Route index element={<Navigate to={ORG_OVERVIEW_ROUTE} replace />} />
        </Route>
        <Route
          path={BILLING_ROUTE}
          element={
            <BillingSettings
              tabs={
                <>
                  <PlanDetailsTab />
                  {companyBillingAndPricingEnabled && <PayerSettingsTab />}
                  {companyBillingAndPricingEnabled && <BillingSettingsTab />}
                  {!companyBillingAndPricingEnabled && <PaymentSettingsTab />}
                  {!companyBillingAndPricingEnabled && <BillingContactTab />}
                  <RecentPaymentsTab />
                </>
              }
            />
          }
        >
          <Route path={BILLING_PLAN_DETAILS_ROUTE} element={<PlanDetails />} />
          <Route path={PAYER_SETTINGS_ROUTE} element={<PayerSettings />} />
          <Route path={BILLING_SETTINGS_ROUTE} element={<BillingSettingsV2 />} />
          <Route path={BILLING_PAYMENT_SETTINGS_ROUTE} element={<PaymentSettings />} />
          <Route path={BILLING_BILLING_CONTACT_ROUTE} element={<BillingContact />} />
          <Route path={BILLING_RECENT_PAYMENTS_ROUTE} element={<RecentPayments />} />
          <Route index element={<Navigate to={BILLING_PLAN_DETAILS_ROUTE} replace />} />
        </Route>
        <Route
          path={TRANSACTIONS_ROUTE}
          element={
            <TransactionSettings
              tabs={
                <>
                  <SignerSettingsTab />
                  <NotaryInstructionsTab />
                </>
              }
            />
          }
        >
          <Route path={TRANSACTIONS_SIGNER_SETTINGS_ROUTE} element={<SignerSettings />} />
          <Route path={TRANSACTIONS_NOTARY_INSTRUCTIONS_ROUTE} element={<NotaryInstructions />} />
          <Route index element={<Navigate to={TRANSACTIONS_SIGNER_SETTINGS_ROUTE} replace />} />
        </Route>
        <Route path={SECURITY_ROUTE} element={<SecuritySettings />}>
          <Route index element={<LoginSecurity />} />
        </Route>
        <Route path={BRAND_SETTINGS_ROOT_PREFIX} element={<BrandSettingsWrapper />}>
          <Route
            path={BRAND_SETTINGS_MAIN_ROUTE}
            element={
              <BrandSettings
                tabs={
                  <>
                    <LogoAndHeaderTab />
                    <BrandColorsTab />
                    <CustomizeLandingPageTab />
                    {isOrgNotaryBackgroundsEnabled && <NotaryBackgroundTab />}
                  </>
                }
              />
            }
          >
            <Route path={LOGO_HEADER_BRAND_ROUTE} element={<LogoAndHeaderBrandSettings />} />
            <Route path={BRAND_COLORS_ROUTE} element={<BrandColorsSettings />} />
            <Route
              path={BRAND_SETTINGS_CUSTOMIZE_LANDING_ROUTE}
              element={<CustomizeLandingPage />}
            />
            {isOrgNotaryBackgroundsEnabled && (
              <Route path={NOTARY_BG_ROUTE} element={<NotaryBackgroundSettings />} />
            )}
            <Route index element={<Navigate to={LOGO_HEADER_BRAND_ROUTE} replace />} />
          </Route>
          <Route index element={<Navigate replace to={BRAND_SETTINGS_MAIN_ROUTE} />} />
        </Route>
        <Route path={INTEGRATIONS_ROUTE} element={<IntegrationSettingsContainer />}>
          <Route index element={<IntegrationSettings />} />
        </Route>

        <Route
          path={API_KEYS_SETTINGS_ROOT_PREFIX}
          element={
            hasPermissionFor("viewOrganizationApiKeys") ? (
              <ApiKeys showApi={props.showApi} />
            ) : (
              <Navigate to="/" replace />
            )
          }
        />

        <Route path={OAUTH_SETTINGS_ROOT_PREFIX} element={<OAuthSettings />} />

        <Route path={TERMS_ROUTE} element={<TermsSettings />}>
          <Route index element={<TermsAndConditions />} />
        </Route>

        <Route index element={<Navigate replace to={`${ORG_ROUTE}/${ORG_OVERVIEW_ROUTE}`} />} />
      </Routes>
    </>
  );
}
